/**
 * The Sales tab's endpoints (routes/sales.ts, sales-review.ts, dashboards.ts).
 * Admin > Sales data keeps its own client in salesApi.ts.
 *
 * Every analytics call sends the dashboard filters as query parameters in the
 * same form the URL hash uses (@mfs/shared salesFiltersToParams), so the
 * server answers exactly what the screen asked.
 */

import {
  salesFiltersToParams,
  type DigestCadence,
  type InsightAction,
  type InsightAudience,
  type InsightEvidence,
  type InsightSeverity,
  type Pace,
  type SalesSiteScope,
  type TargetMetric,
  type TargetPeriodType,
  type TargetRow,
  type TrafficTotals,
  type DashboardLayout,
  type DashboardPresetKey,
  type DateRange,
  type SalesFilters,
  type SalesGranularity,
  type SalesTotals,
  type UserRole,
} from '@mfs/shared';
import { request } from './api';

export interface CoverageEntry {
  channelId: string;
  name: string;
}

export interface ExcludedEntry extends CoverageEntry {
  reason: string;
  detail: string | null;
  affects: 'current' | 'compare' | 'both';
}

export interface Envelope<T> {
  filters: SalesFilters;
  today: string;
  range: DateRange;
  compareRange: DateRange | null;
  granularity: SalesGranularity;
  coverage: {
    included: CoverageEntry[];
    excluded: ExcludedEntry[];
    partial: Array<CoverageEntry & { detail: string; coveredShare: number }>;
    dataThrough: string | null;
  };
  review: { orders: number; amountAtRiskMinor: number; excludedPendingMinor: number };
  data: T;
}

export interface SeriesPoint {
  bucket: string;
  totals: SalesTotals;
}

export interface Overview {
  current: SalesTotals;
  compare: SalesTotals | null;
  series: SeriesPoint[];
  compareSeries: SeriesPoint[] | null;
}

export interface SplitSeries {
  groups: string[];
  points: Array<{ bucket: string; groups: Record<string, SalesTotals> }>;
}

export interface TimeseriesSplit {
  split: 'site' | 'channel';
  labels: Record<string, string>;
  current: SplitSeries;
  compare: SplitSeries | null;
}

export interface BreakdownRow {
  key: string;
  label: string;
  current: SalesTotals;
  compare: SalesTotals | null;
}

export interface ProductLine {
  key: string;
  productId: string | null;
  sku: string | null;
  title: string;
  netSalesMinor: number;
  units: number;
  orders: number;
  compare: { netSalesMinor: number; units: number; orders: number } | null;
}

export interface OrderRow {
  id: string;
  channelId: string;
  orderNumber: string;
  placedAt: string;
  placedDate: string;
  status: string;
  state: string;
  totalMinor: number;
  paidMinor: number;
  refundedMinor: number;
  recognizedMinor: number;
  reviewStatus: 'clean' | 'flagged' | 'resolved';
  treatment: string;
  region: string | null;
  scopeNetSalesMinor: number;
  scopeUnits: number;
  lines: number;
  firstTitle: string | null;
}

export interface OpenOrders {
  count: number;
  valueMinor: number;
  outstandingMinor: number;
  olderThanWeek: number;
  ages: Array<{ key: string; label: string; count: number; valueMinor: number; outstandingMinor: number }>;
  stale: { count: number; valueMinor: number; afterDays: number };
  oldest: Array<{
    id: string;
    channelId: string;
    channel: string;
    orderNumber: string;
    placedAt: string;
    ageDays: number;
    status: string;
    totalMinor: number;
    outstandingMinor: number;
  }>;
}

export interface ChannelHealth {
  channelId: string;
  name: string;
  status: 'healthy' | 'importing_history' | 'stale' | 'failing' | 'not_importing' | 'not_built' | 'disabled';
  message: string;
  earliest: string | null;
  dataThrough: string | null;
  lastSuccessAt: string | null;
  backfillPct: number | null;
  gaps: number;
  error: string | null;
}

export interface SalesMeta {
  today: string;
  channels: Array<{ id: string; name: string; type: string; enabled: boolean }>;
  reviewOpenOrders: number;
  canResolve: boolean;
  canAdmin: boolean;
}

export interface ProductDetail {
  key: string;
  product: {
    id: string;
    sku: string;
    title: string;
    imageUrl: string | null;
    listings: Array<{ channelId: string; priceMinor: number | null; status: string }>;
  } | null;
  title: string;
  current: SalesTotals;
  compare: SalesTotals | null;
  series: SeriesPoint[];
  byChannel: Array<{ channelId: string | null; label: string | null; totals: SalesTotals }>;
  orders: OrderRow[];
  priceChanges: Array<{ at: string; channelId: string; oldValue: string | null; newValue: string | null }>;
}

// ---- review -------------------------------------------------------------------

export type Treatment = 'count_payments' | 'count_order_total' | 'count_custom' | 'exclude';

export interface ReviewIssueSummary {
  id: string;
  ruleId: string;
  label: string;
  severity: 'conflict' | 'warning' | 'info';
  message: string;
  status: 'open' | 'resolved' | 'ignored' | 'auto_cleared';
  amountAtRiskMinor: number;
  defaultTreatment: Treatment | null;
  changedSinceDecision: boolean;
  resolutionRule: { id: string; name: string } | null;
}

export interface ReviewRow {
  orderId: string;
  orderNumber: string;
  channel: { id: string; name: string };
  placedDate: string;
  status: string;
  state: string;
  totalMinor: number;
  paidMinor: number;
  refundedMinor: number;
  recognizedMinor: number;
  treatment: Treatment;
  reviewStatus: 'clean' | 'flagged' | 'resolved';
  override: { treatment: Treatment; decidedById: string | null; decidedAt: string } | null;
  amountAtRiskMinor: number;
  issues: ReviewIssueSummary[];
}

export interface ReviewQueue {
  total: number;
  page: number;
  pageSize: number;
  rows: ReviewRow[];
  facets: Array<{ ruleId: string; label: string; severity: string; orders: number; amountAtRiskMinor: number }>;
  rules: Array<{ id: string; label: string; severity: string; description: string }>;
}

export interface ReviewQueueQuery {
  status: 'open' | 'decided' | 'all';
  ruleId?: string;
  severity?: string;
  channelId?: string;
  site?: string;
  year?: string;
  q?: string;
  sort: 'amount' | 'newest' | 'oldest';
  page: number;
}

export interface OrderDetail {
  id: string;
  channel: { id: string; name: string; type: string };
  externalId: string;
  orderNumber: string | null;
  adminUrl: string | null;
  placedAt: string;
  placedDate: string;
  shippedAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  state: string;
  channelStatus: string;
  shipToRegion: string | null;
  money: {
    itemsGrossMinor: number;
    discountMinor: number;
    shippingMinor: number;
    taxMinor: number;
    totalMinor: number;
    paidMinor: number;
    refundedMinor: number;
    paymentState: string;
    treatment: Treatment;
    countable: boolean;
    recognizedMinor: number;
    netSalesMinor: number;
    recognizedShippingMinor: number;
    recognizedTaxMinor: number;
    feesMinor: number | null;
  };
  reviewStatus: 'clean' | 'flagged' | 'resolved';
  items: Array<{
    id: string;
    title: string;
    sku: string | null;
    quantity: number;
    unitPriceMinor: number;
    grossMinor: number;
    product: { id: string; skuDisplay: string; title: string } | null;
    site: string;
    attributionSource: string;
    netSalesMinor: number;
    discountMinor: number;
    refundedMinor: number;
  }>;
  payments: Array<{ id: string; amountMinor: number; receivedAt: string; method: string | null; status: string }>;
  adjustments: Array<{ id: string; kind: string; amountMinor: number; occurredAt: string; reason: string | null }>;
  issues: Array<{
    id: string;
    ruleId: string;
    severity: 'conflict' | 'warning' | 'info';
    message: string;
    evidence: Record<string, unknown>;
    amountAtRiskMinor: number;
    defaultTreatment: Treatment | null;
    status: 'open' | 'resolved' | 'ignored' | 'auto_cleared';
    resolutionRule: { id: string; name: string } | null;
    resolvedById: string | null;
    resolvedAt: string | null;
    note: string | null;
    changedSinceDecision: boolean;
  }>;
  override: {
    treatment: Treatment;
    customAmountMinor: number | null;
    siteOverride: 'mlf' | 'mff' | null;
    note: string;
    decidedById: string | null;
    decidedAt: string;
    changedSinceDecision: boolean;
  } | null;
  history: Array<{
    id: string;
    action: string;
    treatment: Treatment | null;
    customAmountMinor: number | null;
    siteOverride: string | null;
    ruleIds: string[];
    note: string | null;
    actor: string | null;
    createdAt: string;
  }>;
}

export interface ResolutionRuleSummary {
  id: string;
  name: string;
  treatment: Treatment;
  note: string;
  enabled: boolean;
  createdById: string | null;
  createdAt: string;
  applied: number;
}

// ---- dashboards -----------------------------------------------------------------

export interface LayoutSummary {
  id: string;
  scope: 'personal' | 'shared';
  name: string;
  presetKey: DashboardPresetKey | null;
  version: number;
  audienceRoles: UserRole[];
  baseLayoutId: string | null;
  updatedAt: string;
  editable: boolean;
}

/** Which dashboard is open (lib/dashboards.ts on the API). */
export interface ActiveDashboard {
  source: 'personal' | 'shared' | 'preset';
  layoutId: string | null;
  presetKey: DashboardPresetKey | null;
  name: string;
  version: number | null;
  layout: DashboardLayout;
  editable: boolean;
  baseUpdated: { layoutId: string | null; presetKey: DashboardPresetKey | null; name: string; version: number } | null;
  changes: string[];
}

export interface DashboardsResponse {
  active: ActiveDashboard;
  personal: LayoutSummary[];
  shared: LayoutSummary[];
  lastFilters: Record<string, string> | null;
}

// ---- targets, pace and forecasts (phase 4) -----------------------------------------

export interface CumulativePoint {
  date: string;
  actual: number | null;
  target: number | null;
  forecast: number | null;
  lower: number | null;
  upper: number | null;
}

export interface PaceResult {
  period: { type: TargetPeriodType; start: string; label: string; range: DateRange };
  elapsedDays: number;
  totalDays: number;
  target: { value: number; source: 'set' | 'months' } | null;
  pace: Pace;
  forecast: {
    forecast: number;
    lower: number;
    upper: number;
    method: 'trailing' | 'blended';
    band: 'backtest' | 'variance';
    backtests: number;
    growth: number | null;
    trailingDaily: number;
  } | null;
  cumulative: CumulativePoint[];
}

export interface PaceResponse {
  filters: SalesFilters;
  today: string;
  coverage: Envelope<unknown>['coverage'];
  scopeKey: string | null;
  metric: TargetMetric;
  historyFrom: string | null;
  data: PaceResult;
}

export interface AccuracyCheck {
  periodStart: string;
  label: string;
  day: number;
  forecast: number;
  actual: number;
  error: number | null;
}

export interface AccuracyResponse {
  filters: SalesFilters;
  today: string;
  coverage: Envelope<unknown>['coverage'];
  data: {
    periodType: 'month' | 'quarter';
    metric: TargetMetric;
    checkpoints: number[];
    recorded: AccuracyCheck[];
    simulated: AccuracyCheck[];
    meanError: { recorded: number | null; simulated: number | null };
    historyFrom: string | null;
  };
}

export interface TargetsResponse {
  year: number;
  today: string;
  scopes: Array<{ key: string; label: string }>;
  targets: TargetRow[];
  /** scopeKey → month start → actuals. */
  actuals: Record<string, Record<string, { net_sales: number; orders: number; units: number }>>;
  importing: string[];
  notImporting: string[];
}

export type TargetInput = Omit<TargetRow, 'value'> & { value: number | null };

// ---- insights ------------------------------------------------------------------------

export interface Insight {
  id: string;
  ruleId: string;
  severity: InsightSeverity;
  audience: InsightAudience[];
  site: 'mlf' | 'mff' | null;
  title: string;
  message: string;
  evidence: InsightEvidence[];
  action: InsightAction | null;
  score: number;
  dismissable: boolean;
  firstSeenAt: string;
  lastSeenAt: string;
  dismissedAt: string | null;
  snoozedUntil: string | null;
}

export interface InsightsResponse {
  insights: Insight[];
  total: number;
  hiddenCount: number;
  refreshedAt: string | null;
}

// ---- listings --------------------------------------------------------------------------

export type PriceVerdict = 'too_early' | 'too_little_data' | 'down' | 'up' | 'held' | 'no_lift';

export interface PriceImpact {
  event: {
    listingId: string;
    channelId: string;
    productId: string | null;
    sku: string | null;
    title: string;
    date: string;
    oldPriceMinor: number;
    newPriceMinor: number;
    source: 'app' | 'observed';
    by: string | null;
  };
  before: { days: number; units: number; netMinor: number };
  after: { days: number; units: number; netMinor: number };
  channelRatio: number;
  expectedAfterUnits: number;
  relativeChange: number | null;
  z: number;
  verdict: PriceVerdict;
  monthlyGainMinor: number | null;
}

export interface DeadStockReport {
  days: number;
  since: string;
  judged: CoverageEntry[];
  notJudged: ExcludedEntry[];
  count: number;
  products: Array<{
    key: string;
    productId: string | null;
    sku: string | null;
    title: string;
    listings: Array<{ channelId: string; priceMinor: number | null }>;
    lastSold: string | null;
    unitsBefore: number;
    listedSince: string;
  }>;
}

// ---- emailed reports ---------------------------------------------------------------------

export interface DigestSubscription {
  cadence: DigestCadence;
  enabled: boolean;
  presetKey: InsightAudience;
  site: SalesSiteScope;
  lastSentAt: string | null;
  lastError: string | null;
}

// ---- website traffic (phase 5) --------------------------------------------------------

export interface TrafficEnvelope<T> {
  filters: SalesFilters;
  today: string;
  requestedRange: DateRange;
  /** Where the traffic runs to — usually ending yesterday; null when no site can be counted. */
  range: DateRange | null;
  compareRange: DateRange | null;
  granularity: SalesGranularity;
  through: string | null;
  coverage: {
    included: CoverageEntry[];
    excluded: Array<CoverageEntry & { reason: 'not_connected' | 'orders_excluded' | 'traffic_missing'; detail: string | null; affects: string }>;
    connectedAny: boolean;
  };
  /** Per included site: whether it sends GA4 ecommerce events. */
  ecommerce: Record<string, boolean>;
  data: T | null;
}

export interface TrafficPoint {
  bucket: string;
  totals: TrafficTotals;
}

export interface TrafficOverview {
  current: TrafficTotals;
  compare: TrafficTotals | null;
  perSite: Record<string, TrafficTotals>;
  comparePerSite: Record<string, TrafficTotals> | null;
  series: TrafficPoint[];
  compareSeries: TrafficPoint[] | null;
  siteSeries: Record<string, TrafficPoint[]>;
}

export interface TrafficSources {
  by: 'channel_group' | 'source_medium';
  totalSessions: number;
  others: number;
  rows: Array<{
    key: string;
    sessions: number;
    share: number;
    addToCarts: number;
    gaPurchases: number;
    gaRevenueMinor: number;
    compare: { sessions: number; gaPurchases: number } | null;
  }>;
}

export interface TrafficItems {
  sort: 'views' | 'conversion';
  itemIdMatches: 'sku' | 'external_id';
  withoutEvents: string[];
  unmatched: number;
  items: Array<{
    channelId: string;
    itemId: string;
    productId: string | null;
    sku: string | null;
    title: string;
    views: number;
    adds: number;
    gaPurchases: number;
    units: number | null;
    addRate: number | null;
    soldPerView: number | null;
  }>;
}

// ---- calls ------------------------------------------------------------------------

function qs(filters: SalesFilters, extra: Record<string, string | number | undefined> = {}): string {
  const params = salesFiltersToParams(filters);
  for (const [key, value] of Object.entries(extra)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

const post = <T>(path: string, body: unknown) => request<T>(path, { method: 'POST', body: JSON.stringify(body) });

export const salesDashboardApi = {
  meta: () => request<SalesMeta>('/api/sales/meta'),
  overview: (f: SalesFilters) => request<Envelope<Overview>>(`/api/sales/overview${qs(f)}`),
  timeseries: (f: SalesFilters, split: 'site' | 'channel') =>
    request<Envelope<TimeseriesSplit>>(`/api/sales/timeseries${qs(f, { split })}`),
  breakdown: (f: SalesFilters, dimension: string) =>
    request<Envelope<{ dimension: string; rows: BreakdownRow[] }>>(`/api/sales/breakdown${qs(f, { dimension })}`),
  heatmap: (f: SalesFilters) =>
    request<Envelope<{ cells: Array<{ weekday: number; hour: number; orders: number; netSalesMinor: number }> }>>(
      `/api/sales/heatmap${qs(f)}`,
    ),
  seasonality: (f: SalesFilters, years: number) =>
    request<
      Envelope<{
        throughMonth: number;
        years: Array<{ year: number; complete: boolean; months: Array<{ month: number; orders: number; netSalesMinor: number }> }>;
      }>
    >(`/api/sales/seasonality${qs(f, { years })}`),
  products: (f: SalesFilters, sort: string, limit: number) =>
    request<Envelope<{ sort: string; products: ProductLine[] }>>(`/api/sales/products${qs(f, { sort, limit })}`),
  movers: (f: SalesFilters, limit: number) =>
    request<Envelope<{ comparing: boolean; risers: ProductLine[]; fallers: ProductLine[] }>>(
      `/api/sales/movers${qs(f, { limit })}`,
    ),
  product: (f: SalesFilters, key: string) => request<Envelope<ProductDetail>>(`/api/sales/product${qs(f, { key })}`),
  orders: (f: SalesFilters, extra: Record<string, string | number | undefined>) =>
    request<Envelope<{ range: DateRange; page: number; pageSize: number; total: number; orders: OrderRow[] }>>(
      `/api/sales/orders${qs(f, extra)}`,
    ),
  order: (id: string) => request<OrderDetail>(`/api/sales/orders/${encodeURIComponent(id)}`),
  openOrders: (f: SalesFilters) => request<Envelope<OpenOrders>>(`/api/sales/open-orders${qs(f)}`),
  dataHealth: () => request<{ channels: ChannelHealth[] }>('/api/sales/data-health'),

  reviewQueue: (q: ReviewQueueQuery) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(q)) if (value !== undefined && value !== '') params.set(key, String(value));
    return request<ReviewQueue>(`/api/sales/review?${params.toString()}`);
  },
  reviewSummary: () =>
    request<{ orders: number; amountAtRiskMinor: number; excludedPendingMinor: number; oldestSince: string | null }>(
      '/api/sales/review/summary',
    ),
  reviewRules: () => request<{ rules: ResolutionRuleSummary[] }>('/api/sales/review/rules'),
  resolve: (orderId: string, body: { treatment: Treatment; customAmountMinor?: number; siteOverride?: 'mlf' | 'mff' | null; note: string }) =>
    post<{ order: OrderDetail }>(`/api/sales/review/${encodeURIComponent(orderId)}/resolve`, body),
  ignore: (orderId: string, body: { ruleIds: string[]; note: string }) =>
    post<{ order: OrderDetail }>(`/api/sales/review/${encodeURIComponent(orderId)}/ignore`, body),
  reopen: (orderId: string, note?: string) =>
    post<{ order: OrderDetail }>(`/api/sales/review/${encodeURIComponent(orderId)}/reopen`, { note }),
  bulk: (
    body:
      | { action: 'resolve'; orderIds: string[]; treatment: Exclude<Treatment, 'count_custom'>; note: string }
      | { action: 'ignore'; orderIds: string[]; ruleIds: string[]; note: string },
  ) => post<{ applied: number; skipped: Array<{ orderId: string; reason: string }> }>('/api/sales/review/bulk', body),

  dashboards: () => request<DashboardsResponse>('/api/dashboards'),
  createDashboard: (body: { name: string; layout: DashboardLayout; presetKey?: DashboardPresetKey; baseLayoutId?: string }) =>
    post<{ layout: LayoutSummary; changes: string[] }>('/api/dashboards', body),
  updateDashboard: (
    id: string,
    body: { version: number; name?: string; layout?: DashboardLayout; audienceRoles?: UserRole[]; acknowledgeBase?: boolean },
  ) => request<{ layout: LayoutSummary; changes: string[] }>(`/api/dashboards/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteDashboard: (id: string) => request<{ ok: true }>(`/api/dashboards/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  publishDashboard: (body: {
    name: string;
    layout: DashboardLayout;
    audienceRoles: UserRole[];
    presetKey?: DashboardPresetKey;
    replaceLayoutId?: string;
  }) => post<{ layout: LayoutSummary; changes: string[] }>('/api/admin/dashboards/publish', body),
  pace: (f: SalesFilters, period: TargetPeriodType, metric: TargetMetric) =>
    request<PaceResponse>(`/api/sales/pace${qs(f, { period, metric })}`),
  forecastAccuracy: (f: SalesFilters, period: 'month' | 'quarter', metric: TargetMetric) =>
    request<AccuracyResponse>(`/api/sales/forecast/accuracy${qs(f, { period, metric })}`),
  targets: (year: number) => request<TargetsResponse>(`/api/sales/targets?year=${year}`),
  saveTargets: (targets: TargetInput[]) =>
    request<{ saved: number }>('/api/admin/sales/targets', { method: 'PUT', body: JSON.stringify({ targets }) }),

  insights: (q: { audience?: InsightAudience; site: SalesSiteScope; limit: number; hidden?: boolean }) =>
    request<InsightsResponse>(
      `/api/sales/insights?${new URLSearchParams({
        site: q.site,
        limit: String(q.limit),
        ...(q.audience ? { audience: q.audience } : {}),
        ...(q.hidden ? { hidden: 'true' } : {}),
      }).toString()}`,
    ),
  dismissInsight: (id: string) => post<{ ok: true }>(`/api/sales/insights/${encodeURIComponent(id)}/dismiss`, {}),
  snoozeInsight: (id: string, days: number) => post<{ ok: true }>(`/api/sales/insights/${encodeURIComponent(id)}/snooze`, { days }),
  restoreInsight: (id: string) => post<{ ok: true }>(`/api/sales/insights/${encodeURIComponent(id)}/restore`, {}),
  refreshInsights: () => post<{ created: number; resolved: number; candidates: number }>('/api/admin/sales/insights/refresh', {}),

  priceImpact: (site: SalesSiteScope, days: number) =>
    request<{ since: string; channels: CoverageEntry[]; impacts: PriceImpact[] }>(`/api/sales/price-impact?site=${site}&days=${days}`),
  deadStock: (site: SalesSiteScope, limit = 100) => request<DeadStockReport>(`/api/sales/dead-stock?site=${site}&limit=${limit}`),

  digests: () => request<{ email: string; subscriptions: DigestSubscription[] }>('/api/me/sales-digests'),
  saveDigests: (subscriptions: Array<Pick<DigestSubscription, 'cadence' | 'enabled' | 'presetKey' | 'site'>>) =>
    request<{ ok: true }>('/api/me/sales-digests', { method: 'PUT', body: JSON.stringify({ subscriptions }) }),
  previewDigest: (q: { cadence: DigestCadence; presetKey: InsightAudience; site: SalesSiteScope }) =>
    request<{ subject: string; text: string; html: string }>(`/api/me/sales-digests/preview?${new URLSearchParams(q).toString()}`),
  testDigest: (q: { cadence: DigestCadence; presetKey: InsightAudience; site: SalesSiteScope }) =>
    post<{ ok: boolean; message: string }>('/api/me/sales-digests/test', q),

  trafficOverview: (f: SalesFilters) => request<TrafficEnvelope<TrafficOverview>>(`/api/sales/traffic/overview${qs(f)}`),
  trafficSources: (f: SalesFilters, by: string, limit: number) =>
    request<TrafficEnvelope<TrafficSources>>(`/api/sales/traffic/sources${qs(f, { by, limit })}`),
  trafficItems: (f: SalesFilters, sort: string, limit: number) =>
    request<TrafficEnvelope<TrafficItems>>(`/api/sales/traffic/items${qs(f, { sort, limit })}`),

  setPref: (body: { activeLayoutId?: string | null; activePresetKey?: DashboardPresetKey | null; lastFilters?: Record<string, string> | null }) =>
    request<{ ok: true }>('/api/me/dashboard-pref', { method: 'PUT', body: JSON.stringify(body) }),
};
